import { z } from 'zod';
import { validateCPFCNPJ } from '../../helpers/utils';

const parseDate = (dateString: string) => {
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
    ) {
        return date;
    }
    return null;
};

export type AuthModalContentIds =
    | 'signup'
    | 'login'
    | 'reset-password'
    | 'email-submitted';

export interface AuthModalContentProps {
    setCurrentColumn: (d: AuthModalContentIds) => void;
    setIsOpen: (d: boolean) => void;
}

const cpfFieldZod = z
    .string()
    .min(1, { message: 'Campo obrigatório' })
    .refine(val => validateCPFCNPJ(val), { message: 'CPF ou CNPJ inválido' });

export const signUpPageSchema = z
    .object({
        name: z.string().min(1, { message: 'Campo obrigatório' }),
        birthDate: z
            .string()
            .min(10, { message: 'Campo obrigatório' })
            .refine(
                value => {
                    const date = parseDate(value);
                    return date !== null;
                },
                { message: 'Data de nascimento inválida' }
            ),
        email: z
            .string()
            .email({ message: 'Email inválido' })
            .min(1, { message: 'Campo obrigatório' }),
        cpf: cpfFieldZod,
        phone: z.string().min(13, { message: 'Campo obrigatório' }),
        phoneCountry: z.string().min(2, { message: 'Campo obrigatório' }),
        password: z
            .string()
            .min(6, { message: 'A senha deve ter no mínimo 6 caracteres' }),
        confirm_password: z.string().min(6, {
            message: 'A confirmação de senha deve ter no mínimo 6 caracteres',
        }),
        // address: z.string().min(1, { message: 'Campo obrigatório' }),
    })
    .superRefine((data, ctx) => {
        if (data.password.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Campo obrigatório',
                path: ['password'],
            });
        }
        if (data.password !== data.confirm_password) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'As senhas não conferem',
                path: ['confirm_password'],
            });
        }
    });

export const loginPageSchema = z.object({
    login: cpfFieldZod,
    password: z.string().min(1, { message: 'Campo obrigatório' }),
});

export const resetPasswordPageSchema = z.object({
    login: cpfFieldZod,
});

const cpfFieldZodEn = z.string().min(1, { message: 'Required field' });

export const signUpPageSchemaEn = z
    .object({
        name: z.string().min(1, { message: 'Required field' }),
        birthDate: z
            .string()
            .min(10, { message: 'Required field' })
            .refine(
                value => {
                    const date = parseDate(value);
                    return date !== null;
                },
                { message: 'Invalid birthdate' }
            ),
        email: z
            .string()
            .email({ message: 'Invalid email' })
            .min(1, { message: 'Required field' }),
        cpf: cpfFieldZodEn,
        phone: z.string().min(7, { message: 'Required field' }),
        phoneCountry: z.string().min(2, { message: 'Required field' }),
        password: z
            .string()
            .min(6, { message: 'Password must be at least 6 characters long' }),
        confirm_password: z.string().min(6, {
            message: 'Password confirmation must be at least 6 characters long',
        }),
        // address: z.string().min(1, { message: 'Required field' }),
    })
    .superRefine((data, ctx) => {
        if (data.password.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required field',
                path: ['password'],
            });
        }
        if (data.password !== data.confirm_password) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Passwords do not match',
                path: ['confirm_password'],
            });
        }
    });

export const loginPageSchemaEn = z.object({
    login: cpfFieldZodEn,
    password: z.string().min(1, { message: 'Required field' }),
});

export const resetPasswordPageSchemaEn = z.object({
    login: cpfFieldZodEn,
});
